import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Chip,
  Alert,
  AlertTitle,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  PeopleAlt as PeopleAltIcon,
  Bolt as BoltIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WhatsAppService from '../../services/whatsapp.service';
import { showWebNotification } from '../../utils/webNotifications';

interface TrackerItem {
  id: number;
  phoneNumber: string;
  contactId: number | null;
  clientName: string | null;
  companyName: string | null;
  state: string;
  stageKey: string;
  stepNumber: number;
  totalSteps: number;
  language: string | null;
  serviceSelected: string | null;
  serviceType: string | null;
  requestNumber: string | null;
  requestId: number | null;
  requestCreatedAt: string | null;
  lastActivityAt: string | null;
  minutesSinceActivity: number | null;
  isActive: boolean;
  isStale: boolean;
}

interface ServiceStatus {
  configured: boolean;
  connected: boolean;
  phone: string | null;
  qualityRating: string | null;
  accountMode?: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  failedLast24h: number;
  checkedAt: string;
  error: string | null;
}

const REFRESH_MS = 15000;

const STAGE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  idle: 'default',
  vehicle_identification: 'info',
  before_photo: 'info',
  location: 'primary',
  reason: 'primary',
  choosing_option: 'warning',
  profile: 'secondary',
  request_created: 'success',
  unknown: 'default',
};

const WhatsAppTracker: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<TrackerItem[]>([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, stale: 0 });
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const prevConnectedRef = useRef<boolean | null>(null);

  const fetchTracker = useCallback(async () => {
    try {
      const response = await WhatsAppService.getTracker();
      setItems(response.data?.data || []);
      setSummary(response.data?.summary || { total: 0, active: 0, stale: 0 });
    } catch (error) {
      console.error('Error loading WhatsApp tracker:', error);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await WhatsAppService.getServiceStatus();
      const data: ServiceStatus = response.data?.data;
      setStatus(data);

      const isDown = !data.configured || !data.connected;
      const wasHealthy = prevConnectedRef.current === true;
      if (isDown && wasHealthy) {
        showWebNotification({
          title: t('whatsappTracker.serviceOffline'),
          message: t('whatsappTracker.serviceOfflineDesc'),
          link: '/operations/whatsapp-tracker',
        });
      }
      prevConnectedRef.current = data.configured && data.connected;
    } catch (error) {
      console.error('Error loading WhatsApp service status:', error);
    }
  }, [t]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchTracker(), fetchStatus()]);
    setLoading(false);
  }, [fetchTracker, fetchStatus]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      refreshAll();
    }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  const formatLastActivity = useCallback(
    (item: TrackerItem) => {
      const mins = item.minutesSinceActivity;
      if (mins === null || mins === undefined) return '-';
      if (mins < 1) return t('whatsappTracker.justNow');
      if (mins < 60) return t('whatsappTracker.minutesAgo', { count: mins });
      if (mins < 60 * 24) return t('whatsappTracker.hoursAgo', { count: Math.floor(mins / 60) });
      return t('whatsappTracker.daysAgo', { count: Math.floor(mins / (60 * 24)) });
    },
    [t]
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/[^0-9]/g, '');
    return items.filter((item) => {
      if (activeOnly && !item.isActive) return false;
      if (!q) return true;
      const phoneDigits = (item.phoneNumber || '').replace(/[^0-9]/g, '');
      const nameMatch = (item.clientName || '').toLowerCase().includes(q);
      const companyMatch = (item.companyName || '').toLowerCase().includes(q);
      const serviceMatch = (item.serviceType || item.serviceSelected || '').toLowerCase().includes(q);
      const phoneMatch = qDigits ? phoneDigits.includes(qDigits) : false;
      return nameMatch || companyMatch || serviceMatch || phoneMatch;
    });
  }, [items, search, activeOnly]);

  const openChat = useCallback(
    (item: TrackerItem) => {
      if (item.contactId) {
        navigate(`/operations/whatsapp-chat/${item.contactId}`);
        return;
      }
      navigate(`/operations/whatsapp-chat?phone=${encodeURIComponent(item.phoneNumber)}`);
    },
    [navigate]
  );

  const stageLabel = (key: string) => t(`whatsappTracker.stages.${key}`, t('whatsappTracker.stages.unknown'));

  const renderServiceBanner = () => {
    if (!status) return null;

    if (!status.configured) {
      return (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
          <AlertTitle>{t('whatsappTracker.serviceNotConfigured')}</AlertTitle>
          {t('whatsappTracker.serviceNotConfiguredDesc')}
          {status.error ? ` (${status.error})` : ''}
        </Alert>
      );
    }

    if (!status.connected) {
      return (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
          <AlertTitle>{t('whatsappTracker.serviceOffline')}</AlertTitle>
          {t('whatsappTracker.serviceOfflineDesc')}
          {status.error ? ` (${status.error})` : ''}
        </Alert>
      );
    }

    return (
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Alert severity="success" icon={<CheckCircleIcon />}>
          <AlertTitle>{t('whatsappTracker.serviceOnline')}</AlertTitle>
          {t('whatsappTracker.serviceOnlineDesc', {
            phone: status.phone || '-',
            quality: status.qualityRating || 'N/A',
          })}
        </Alert>
        {status.failedLast24h > 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />}>
            {t('whatsappTracker.failedWarning', { count: status.failedLast24h })}
          </Alert>
        )}
      </Stack>
    );
  };

  const summaryCard = (
    icon: React.ReactNode,
    label: string,
    value: number,
    color: string
  ) => (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${color}.light`,
            color: `${color}.main`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WhatsAppIcon sx={{ color: '#25D366' }} />
            <Typography variant="h4" fontWeight={700}>
              {t('whatsappTracker.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('whatsappTracker.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label={t('whatsappTracker.autoRefresh')}
          />
          <Tooltip title={t('whatsappTracker.refresh')}>
            <IconButton onClick={refreshAll} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {renderServiceBanner()}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}>
        {summaryCard(<PeopleAltIcon />, t('whatsappTracker.totalConversations'), summary.total, 'info')}
        {summaryCard(<BoltIcon />, t('whatsappTracker.activeConversations'), summary.active, 'success')}
        {summaryCard(<HourglassEmptyIcon />, t('whatsappTracker.staleConversations'), summary.stale, 'warning')}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder={t('whatsappTracker.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={<Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />}
            label={t('whatsappTracker.showActiveOnly')}
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('whatsappTracker.phoneNumber')}</TableCell>
                  <TableCell>{t('whatsappTracker.clientName')}</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>{t('whatsappTracker.stage')}</TableCell>
                  <TableCell>{t('whatsappTracker.language')}</TableCell>
                  <TableCell>{t('whatsappTracker.service')}</TableCell>
                  <TableCell>{t('whatsappTracker.requestNumber')}</TableCell>
                  <TableCell>{t('whatsappTracker.lastActivity')}</TableCell>
                  <TableCell align="right">{t('whatsappTracker.openChat')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      {t('whatsappTracker.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => {
                    const progress =
                      item.totalSteps > 0 ? (item.stepNumber / item.totalSteps) * 100 : 0;
                    const serviceLabel = item.serviceType || item.serviceSelected || '-';

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => openChat(item)}
                            sx={{ fontWeight: 600, textTransform: 'none', px: 0 }}
                            startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} fontSize="small" />}
                          >
                            {item.phoneNumber}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {item.clientName ? (
                            <Stack spacing={0.25}>
                              <Typography variant="body2" fontWeight={500}>
                                {item.clientName}
                              </Typography>
                              {item.companyName && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.companyName}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                label={stageLabel(item.stageKey)}
                                color={STAGE_COLORS[item.stageKey] || 'default'}
                                variant={item.isActive ? 'filled' : 'outlined'}
                              />
                              {item.stepNumber > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('whatsappTracker.step')} {item.stepNumber}/{item.totalSteps}
                                </Typography>
                              )}
                            </Stack>
                            {item.isActive && item.stepNumber > 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {item.language ? item.language.toUpperCase() : '-'}
                        </TableCell>
                        <TableCell>{serviceLabel}</TableCell>
                        <TableCell>
                          {item.requestNumber ? (
                            <Chip size="small" label={item.requestNumber} color="success" variant="outlined" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatLastActivity(item)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('whatsappTracker.openChat')}>
                            <IconButton color="primary" onClick={() => openChat(item)}>
                              <ChatIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default WhatsAppTracker;
